import React, { useMemo, useCallback } from 'react';
import CalendarEventItem from './CalendarEventItem';
import { getMonthCellMultiDaySegment } from '../viewServices/calendar_grid_utils';
import { getOrderedCellEvents } from '../viewServices/calendar_day_cell_order';
import { DAY_HEADING_ROW_PX } from '../viewServices/calendar_week_multiday_layout';
import { PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';

/** Altura fixa de cada chip no mês (+ gap) — independente da quantidade de eventos na célula. */
export const MONTH_CELL_EVENT_ROW_HEIGHT_PX = 17;
const MONTH_CELL_EVENT_GAP_PX = 2;
const MONTH_CELL_VERTICAL_PAD_PX = 4;
/** Espaço reservado para a linha “+n eventos” quando há overflow. */
const MONTH_MORE_ROW_RESERVED_PX = 14;
/** Coluna mensal sem `weekRowHeightPx` prop (fallback). */
const FALLBACK_WEEK_ROW_HEIGHT_PX = 112;

/**
 * Quantidade de chips (altura fixa) que cabem na célula. Com overflow reserva zona para "+n eventos".
 * n chips ocupam `n*(ROW+GAP)-GAP`; com footer ainda há `reservedMore` px.
 */
export function computeMonthCellMaxVisibleSlots(weekRowHeightPx, eventCount = 0) {
  if (!weekRowHeightPx || eventCount <= 0) return 0;
  const usable = Math.max(0, weekRowHeightPx - DAY_HEADING_ROW_PX - MONTH_CELL_VERTICAL_PAD_PX);
  const stride = MONTH_CELL_EVENT_ROW_HEIGHT_PX + MONTH_CELL_EVENT_GAP_PX;

  /** Máximo de chips se não for preciso linha de overflow. */
  const fitAll = Math.max(0, Math.floor((usable + MONTH_CELL_EVENT_GAP_PX) / stride));
  if (fitAll === 0) return Math.min(eventCount, 1);

  if (eventCount <= fitAll) return eventCount;

  /** Com linha "+n eventos" reservada. */
  const fitOverflow = Math.max(
    1,
    Math.floor((usable - MONTH_MORE_ROW_RESERVED_PX + MONTH_CELL_EVENT_GAP_PX) / stride)
  );
  return Math.max(1, Math.min(eventCount, fitOverflow, fitAll));
}

const CalendarWeekDayColumn = React.memo(function CalendarWeekDayColumn({
  date,
  dateKey,
  isCurrentMonth,
  isToday,
  dayEvents = [],
  weekRowHeightPx,
  onDayClick,
  onEventClick,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
  calendarMonthChipMobileUx = false,
}) {
  const cellEvents = useMemo(() => getOrderedCellEvents(dayEvents, false), [dayEvents]);

  const resolvedWeekRowPx =
    typeof weekRowHeightPx === 'number' && weekRowHeightPx > 0
      ? weekRowHeightPx
      : FALLBACK_WEEK_ROW_HEIGHT_PX;

  const maxSlots = computeMonthCellMaxVisibleSlots(resolvedWeekRowPx, cellEvents.length);

  const visibleEvents = cellEvents.slice(0, Math.max(0, maxSlots));
  const remainingCount = Math.max(0, cellEvents.length - visibleEvents.length);

  const handleDayClick = useCallback(() => {
    onDayClick?.(dateKey, date);
  }, [onDayClick, dateKey, date]);

  return (
    <div
      className={`
        flex min-h-0 h-full min-w-0 flex-col overflow-hidden
        border-r border-b border-gray-200
        ${isCurrentMonth ? 'bg-white' : 'bg-gray-100'}
        ${isToday ? 'ring-2 ring-blue-500 ring-inset z-[2]' : ''}
        hover:bg-gray-50
        cursor-pointer
      `}
      onClick={handleDayClick}
    >
      <div
        className={`flex shrink-0 items-start px-1 pt-1 text-xs font-medium leading-none ${
          isToday ? 'text-blue-700 font-semibold' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
        }`}
        style={{ height: DAY_HEADING_ROW_PX, minHeight: DAY_HEADING_ROW_PX }}
      >
        {date.getDate()}
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden px-0.5 pt-px pb-0.5"
        style={{ gap: MONTH_CELL_EVENT_GAP_PX }}
      >
        {visibleEvents.map((event) => (
          <div
            key={event.id}
            className="shrink-0 overflow-hidden"
            style={{
              height: MONTH_CELL_EVENT_ROW_HEIGHT_PX,
              minHeight: MONTH_CELL_EVENT_ROW_HEIGHT_PX,
            }}
          >
            <CalendarEventItem
              compact
              compactMobileMonthCell={calendarMonthChipMobileUx}
              event={event}
              monthSegment={getMonthCellMultiDaySegment(event, dateKey)}
              onClick={onEventClick}
              timeFormatPreference={timeFormatPreference}
            />
          </div>
        ))}

        {remainingCount > 0 ? (
          <div
            className="pointer-events-none flex shrink-0 items-center px-1 text-[10px] tabular-nums leading-tight text-gray-500"
            style={{ minHeight: MONTH_MORE_ROW_RESERVED_PX - 4 }}
            role="note"
          >
            +{remainingCount}
            {!calendarMonthChipMobileUx ? ' eventos' : ''}
          </div>
        ) : null}
      </div>
    </div>
  );
});

CalendarWeekDayColumn.displayName = 'CalendarWeekDayColumn';

export default CalendarWeekDayColumn;
